import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { BleService } from 'src/ble/BleService';
import { AnnotationsContextProvider } from 'src/components/annotations/AnnotationsContext';
import { selectAuthUserInfo } from 'src/redux/auth/selectors';
import { createLogLocalDataByFilenameSelector } from 'src/redux/log/selectors';
import { useSocketGet } from 'src/utils/socket';
import { SocketContextSentinel } from 'src/utils/socket/context/sentinelSocketContext';
import { useBle } from '../../ble/useBle';
import { JobLogDisplayPermissions } from '../../models/jobLog';
import { BUNDLE_DOWNLOAD_MINIMUM_FIRMWARE_VERSION } from '../../utils/feature-flags/FeatureFlagConstants';
import { compareFirmwareVersions } from '../../utils/helpers/firmwareHelpers';
import { AppRouteConstants } from '../app-layout/AppRouteConstants';
import { JobInfo } from './JobInfo';
import type { JobInfoTimeRange } from './types';

/**
 * This is a mobile-only page for when a job is streaming from the Sentinel hand-held controller.
 *
 * The "Filename" refers to a file on the physical controller.
 */
export const JobInfoByFilenamePage: React.FC = () => {
  const params = useParams<{ filename: string }>();
  const filename = window.decodeURIComponent(params.filename);
  const history = useHistory();
  const dataSelector = React.useMemo(() => createLogLocalDataByFilenameSelector(filename), [filename]);
  const data = useSelector(dataSelector);
  const userData = useSelector(selectAuthUserInfo);

  const { item: info } = useSocketGet(SocketContextSentinel, 'logs', data?.id);

  const { firmwareVersion } = useBle();
  // Controller reports firmware as 'CTRL FW V2.0.0' - splitting on V to get the semantic version for comparison
  const parsedFirmwareVersion =
    !!firmwareVersion && firmwareVersion.includes('V') ? firmwareVersion.split('V')[1] : undefined;
  const showEstimatedTimeToCompletion =
    compareFirmwareVersions(parsedFirmwareVersion, BUNDLE_DOWNLOAD_MINIMUM_FIRMWARE_VERSION) >= 0;

  // This route is loaded only for local log files. we can assume user has permissions to see all job info
  const displayPermissions: JobLogDisplayPermissions = useMemo(
    () => ({
      showTimestamps: true,
      showPressures: true,
      showPieCharts: true,
      showIdleVsPressure: true,
      showTubeGraph: true,
      showAnnotations: true,
      showDeleteActions: false,
      showShareActions: false,
      showAnnotationUser: true,
      showAnnotationActions: true,
      showDownloadActions: false,
      showPrintActions: false,
      showEstimatedTimeToCompletion: showEstimatedTimeToCompletion,
    }),
    [showEstimatedTimeToCompletion],
  );

  const showUpdate = data?.pendingClose === false;

  const showComplete = data?.pendingClose === false;
  const onComplete = useMemo(() => {
    if (!showComplete) return undefined;
    return async () => {
      try {
        await BleService.closeLog(filename);
      } catch (err) {
        console.error('Error completing job log:', err);
        throw err;
      }
    };
  }, [filename, showComplete]);

  React.useEffect(() => {
    if (info?.status === 'complete') {
      history.replace(AppRouteConstants.LogsList);
    }
  }, [history, info?.status]);

  const isLimitedMode = data?.pendingClose ?? true;

  const timeRange = useMemo<JobInfoTimeRange>(() => {
    const result: JobInfoTimeRange = {
      min: 0,
      max: data?.data?.msElapsed ?? 1,
    };
    return result;
  }, [data?.data?.msElapsed]);

  const timezone = info?.deviceTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <AnnotationsContextProvider parentId={data?.id} parentClaimKey={filename}>
      <JobInfo
        info={info}
        data={data?.data}
        status={!!data?.pendingClose ? 'complete' : 'live'}
        description={data?.metadata?.description || info?.description || info?.name || info?.originalFileName}
        subheading={undefined}
        siteName={data?.metadata?.siteName || info?.siteName}
        heatExchangerModelNumber={data?.metadata?.heatExchangerModelNumber || info?.heatExchangerModelNumber}
        orgName={userData?.orgDetails?.orgName}
        timezone={timezone}
        onComplete={onComplete}
        showUpdateButton={showUpdate}
        isLimitedMode={isLimitedMode}
        hasErrors={!!data?.hasParseErrors}
        displayPermissions={displayPermissions}
        timeRange={timeRange}
      />
    </AnnotationsContextProvider>
  );
};
